import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown, Trash } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import { TABLE_IDS } from "../../../constants/common";
import DeleteMeetingRoomModal from "../../modal/DeleteMeetingRoomModal";
import { MeetingRoom } from "../../../hooks/common/interface";

interface MeetingRoomsTableProps {
  meetingRooms: MeetingRoom[];
  setSelectedMeetingRoomId: Dispatch<SetStateAction<number>>;
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const MeetingRoomsTable = ({
  meetingRooms,
  setSelectedMeetingRoomId,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: MeetingRoomsTableProps) => {
  const [isDeleteMeetingRoomModalOpen, setIsDeleteMeetingRoomModalOpen] =
    useState(false);
  const [selectedDeleteMeetingRoomId, setSelectedDeleteMeetingRoomId] =
    useState(0);

  const { t, i18n } = useTranslation();

  const handleDeleteMeetingRoom = useCallback(async (locationId: number) => {
    setIsDeleteMeetingRoomModalOpen(true);
    setSelectedDeleteMeetingRoomId(locationId);
  }, []);

  const columns: ColumnDef<MeetingRoom>[] = useMemo(
    () => [
      {
        accessorKey: "n_MeetingRoomID",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("meetingRoomId")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() =>
                setSelectedMeetingRoomId(row.original.n_MeetingRoomID)
              }
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_MeetingRoomID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_MeetingRoomName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("meetingRoomNameEn")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_MeetingRoomName_En}</span>;
        },
      },
      {
        accessorKey: "s_MeetingRoomName_Ar",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("meetingRoomNameAr")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_MeetingRoomName_Ar}</span>;
        },
      },
      {
        accessorKey: "s_Description",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("description")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Description}</span>;
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </span>
          );
        },
      },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() =>
                handleDeleteMeetingRoom(row.original.n_MeetingRoomID)
              }
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteMeetingRoom, i18n.language, setSelectedMeetingRoomId, t]
  );

  return (
    <>
      <TableWrapper
        data={meetingRooms}
        columns={columns}
        isColumnChooser={false}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.MEETING_ROOMS_CONFIG}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteMeetingRoomModal
        meetingRoomId={selectedDeleteMeetingRoomId}
        isOpen={isDeleteMeetingRoomModalOpen}
        onOpenChange={() => setIsDeleteMeetingRoomModalOpen(false)}
      />
    </>
  );
};

export default React.memo(MeetingRoomsTable);
